import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { Check, Info, WandSparkles } from "lucide-react";
import { toast } from "sonner";
import useAuthStore from "stores/useAuthStore";

import { Button } from "@/components/ui/button";
import { SaveButton } from "@/components/ui/save-button";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { InputGroupAddon } from "@/components/ui/input-group";
import { cn } from "@/lib/utils";
import ManageCategoriesModal from "components/modals/ManageCategoriesModal";
import UploadManager from "components/UploadManager";
import RichTextEditor from "components/RichEditor/RichTextEditor";
import { noteToEditorProps } from "components/NoteContent";
import UploadAttachmentList from "components/UploadAttachmentList";
import ActivityService from "services/Activity";
import NoteTemplateService from "services/NoteTemplate";
import { formatError } from "helpers/formatError";
import { useNoteDraft } from "hooks/useNoteDraft";
import { useNoteParentEntity } from "hooks/useNoteParentEntity";

// The form's own overlays render in portals outside the form wrapper. A
// pointer press inside one of them must not count as leaving the form, so
// every surface those overlays can present is listed here.
const OVERLAY_SELECTOR =
  '[role="dialog"], [role="alertdialog"], [data-slot="combobox-content"], [data-slot="popover-content"], [data-slot="dialog-overlay"], [data-slot="alert-dialog-overlay"]';

function isInsideOverlay(element) {
  return !!element?.closest?.(OVERLAY_SELECTOR);
}

// Escape belongs to whatever popup is open first. Lexical's mention menu marks
// the event handled, and a toolbar combobox keeps focus on its own input while
// its list is open, so neither is caught by the overlay check alone.
function isEscapeForPopup(event) {
  return (
    event.defaultPrevented ||
    event.target.getAttribute?.("aria-expanded") === "true" ||
    isInsideOverlay(event.target)
  );
}

// Nothing between the pane and the composer may be positioned, or the
// full-screen `absolute inset-0` fills that element instead of the pane.
const DETAIL_PANE_SELECTOR = '[data-slot="detail-pane"]';

// Loading a plain body into the editor goes through the HTML path, so the
// text has to be escaped or a body containing markup would be parsed as it.
function plainTextToHtml(text) {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .split("\n")
    .map(line => `<p>${line}</p>`)
    .join("");
}

// Inline cue for the localStorage draft lifecycle; "idle" renders nothing.
const DRAFT_STATUS_MESSAGE = {
  idle: null,
  saving: (
    <>
      <Spinner data-icon="inline-start" />
      Saving...
    </>
  ),
  saved: (
    <>
      <Check data-icon="inline-start" className="tw:size-3.5" />
      Draft saved
    </>
  ),
  cleared: "Draft cleared",
};

const MODEL_TYPE_NOUNS = {
  Contact: "contact",
  Entity: "entity",
  Campaign: "campaign",
  Case: "case",
  Task: "task",
  RevenueOpportunity: "revenue opportunity",
  CustomTable: "record",
};

// Picking a template is an action, not a selection: the combobox is remounted
// after each pick so it returns to its placeholder.
function TemplatePicker({
  templates,
  onSelect,
  placeholder,
  className,
  icon: Icon,
}) {
  const [key, setKey] = useState(0);

  const items = useMemo(
    () => templates.map(t => ({ value: t.id, label: t.name })),
    [templates]
  );

  const handleChange = next => {
    if (next) {
      onSelect(next.value);
      setKey(k => k + 1);
    }
  };

  return (
    <Combobox
      key={key}
      items={items}
      onValueChange={handleChange}
      itemToStringLabel={item => item?.label ?? ""}
    >
      <ComboboxInput placeholder={placeholder} className={className}>
        {Icon && (
          <InputGroupAddon align="inline-start">
            <Icon />
          </InputGroupAddon>
        )}
      </ComboboxInput>
      <ComboboxContent className="tw:min-w-52">
        <ComboboxEmpty>No templates found.</ComboboxEmpty>
        <ComboboxList>
          {item => (
            <ComboboxItem key={item.value} value={item}>
              {item.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

TemplatePicker.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  icon: PropTypes.elementType,
};

function NoteForm(props) {
  const {
    note,
    modelId,
    modelType,
    slug,
    callSid,
    refersToModel,
    refersToID,
    onSuccess,
    onCancel,
    onDirtyChange,
    categories = [],
    updateCategories,
  } = props;

  const isEditing = !!(note && note.id);
  const [inDetailPane, setInDetailPane] = useState(false);
  const canFullscreen = inDetailPane;
  const includeInEntityId = useId();
  const [formData, _setFormData] = useState(() =>
    note?.id
      ? {
          categoryId: note.category_id,
          includeInEntity: !!note.info?.include_in_entity,
        }
      : { categoryId: null, includeInEntity: false }
  );
  // Fed only by the manager's progress listener, so it always mirrors
  // uploadManager.uploads.
  const [uploads, setUploads] = useState([]);
  const uploadManager = useMemo(() => new UploadManager(), []);
  // Drafts protect the new-note composer only; editing an existing note
  // keeps the hook inert. The key names the composer, so the activity log's
  // composer and the notes tab's do not share one draft.
  const draftKey = `note-draft:v1:${modelType}:${modelId}:${slug ?? ""}:${refersToModel ?? ""}:${refersToID ?? ""}`;
  const {
    initialDraft,
    status: draftStatus,
    saveDraft,
    clearDraft,
  } = useNoteDraft(draftKey, !isEditing);

  const [editorHasText, setEditorHasText] = useState(!!initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(isEditing || !!initialDraft);
  // A template chosen while collapsed becomes the editor's initial document,
  // because the editor does not exist yet to append to.
  const [templateContent, setTemplateContent] = useState();
  const [manageOpen, setManageOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const editor = useRef();
  const restoredDraftPending = useRef(!!initialDraft);
  const fileInputRef = useRef();
  const formWrapperRef = useRef();

  // A legacy note may hold no stored document at all, only a plain comment or
  // a plain_text body. Those open as text rather than as an empty editor,
  // which would blank the note on the next save.
  const { content: initialContent, editorState: initialEditorState } =
    useMemo(() => {
      if (!note?.id) return {};
      const editorProps = noteToEditorProps(note);
      if (editorProps.editorState || editorProps.content) return editorProps;
      const plain = note.info?.comment || note.plain_text;
      return plain ? { content: plainTextToHtml(plain) } : {};
    }, [note]);
  const noteImportPending = useRef(!!(initialContent || initialEditorState));

  const { data: enabledTemplates = [] } = useQuery({
    queryKey: ["note_templates", "enabled"],
    queryFn: () => NoteTemplateService.getForFilters({ enabled: true }),
  });

  // A note owned by a Contact, Case, or Revenue Opportunity can also surface on
  // its parent entity's timeline. The control names that entity, so it renders
  // only once the entity is resolved and hides when the owner has none (a Case
  // may have no entity).
  const {
    entityName,
    entityId,
    isLoading: isLoadingEntity,
  } = useNoteParentEntity(modelType, modelId);
  const showIncludeInEntity = !isLoadingEntity && !!entityId;

  const setFormData = useCallback(newFormData => {
    _setFormData(formData => ({
      ...formData,
      ...newFormData,
    }));
  }, []);

  const handleEditorStateChange = ({ hasText }) => {
    setEditorHasText(hasText);
    // Loading a document into the editor emits a change of its own. When the
    // composer opened on a restored draft that content is already stored, so
    // saving it again would show a false "Saving..." on every mount.
    if (restoredDraftPending.current) {
      restoredDraftPending.current = false;
      return;
    }
    if (isEditing) {
      // The same applies to the edited note's own document: its load is not a
      // change the user made.
      if (noteImportPending.current) {
        noteImportPending.current = false;
        return;
      }
      onDirtyChange?.(true);
      return;
    }
    if (hasText) {
      saveDraft(() => editor.current.exportState());
    } else {
      clearDraft();
    }
  };

  const collapse = () => {
    setTemplateContent(undefined);
    setIsFullscreen(false);
    setExpanded(false);
  };

  const handleTemplateSelect = templateId => {
    const template = enabledTemplates.find(t => t.id === templateId);
    if (!template?.content_html) return;
    if (expanded) {
      editor.current?.appendHtml(template.content_html);
    } else {
      setTemplateContent(template.content_html);
      setExpanded(true);
    }
    toast.success(`Template "${template.name}" inserted`);
  };

  const isUploading = uploads.some(
    u => u.status === "queued" || u.status === "uploading"
  );
  const hasFailedUploads = uploads.some(u => u.status === "failed");

  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    // Clearing the input lets a removed file be chosen again.
    e.target.value = "";
    if (!files.length) return;
    uploadManager.uploadFiles(files);
    onDirtyChange?.(true);
  };

  const handleFormDataChange = newFormData => {
    setFormData(newFormData);
    onDirtyChange?.(true);
  };

  // Collapse an empty composer when the user presses somewhere outside it.
  // Detecting the press at its origin, before focus moves, is what keeps a
  // control's own overlay from being mistaken for a foreign one: the first
  // press lands on the trigger button, outside the wrapper and not yet an
  // overlay, and only a later press can land inside an open overlay. Full
  // screen is skipped, so collapsing or expanding a sidebar does not end it.
  useEffect(() => {
    if (
      !expanded ||
      isFullscreen ||
      isEditing ||
      editorHasText ||
      uploads.length > 0
    )
      return;
    const handlePointerDown = e => {
      const wrapper = formWrapperRef.current;
      if (!wrapper) return;
      if (wrapper.contains(e.target) || isInsideOverlay(e.target)) return;
      collapse();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [expanded, isFullscreen, isEditing, editorHasText, uploads.length]);

  useLayoutEffect(() => {
    setInDetailPane(
      expanded && !!formWrapperRef.current.closest(DETAIL_PANE_SELECTOR)
    );
  }, [expanded]);

  // Escape unwinds one layer at a time: a full-screen edit returns to the card
  // first, and only the next Escape discards the edit.
  const handleKeyDown = e => {
    if (e.key !== "Escape" || isEscapeForPopup(e)) return;
    if (isFullscreen) {
      setIsFullscreen(false);
      return;
    }
    if (!onCancel) return;
    e.preventDefault();
    onCancel();
  };

  const resetForm = () => {
    clearDraft();
    setFormData({ categoryId: null, includeInEntity: false });
    uploadManager.removeUploads();
    setUploads([]);
    editor.current?.resetState();
    if (!isEditing) collapse();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    if (isUploading) {
      toast.error("Wait for the attachments to finish uploading");
      return;
    }
    if (hasFailedUploads) {
      toast.error("Retry or remove the failed attachments before saving");
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        model_id: modelId,
        model_type: modelType,
        editor_state: JSON.stringify(editor.current.exportState()),
        plain_text: editor.current.exportStateAsPlain(),
        rich_text: editor.current.exportStateAsHtml(),
        category_id: formData.categoryId,
        info: {
          refers_to_model: refersToModel,
          refers_to_id: refersToID,
          include_in_entity: formData.includeInEntity,
          call_sid: callSid,
          mentioned_users: editor.current.getMentionedUsers(),
        },
        creator_user_id: useAuthStore.getState().userAuth.id,
        attachments: uploads
          .filter(u => u.status === "finished")
          .map(u => u.attachment),
      };

      const slugOpts = slug ? { slug, modelId } : undefined;
      if (isEditing) {
        await ActivityService.updateActivityLog(note.id, data, slugOpts);
      } else {
        await ActivityService.createActivityLog(data, slugOpts);
      }

      toast.success(isEditing ? "Note updated" : "Note created");
      resetForm();
      if (onSuccess) await onSuccess();
    } catch (error) {
      toast.error(formatError(error, "Failed to save note"));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const listenerId = uploadManager.addProgressListener(next =>
      setUploads([...next])
    );
    return () => uploadManager.removeProgressListener(listenerId);
  }, [uploadManager]);

  const fullscreenColumnClass = isFullscreen
    ? "tw:flex tw:min-h-0 tw:flex-1 tw:flex-col"
    : undefined;

  const noun = MODEL_TYPE_NOUNS[modelType] || "record";
  const placeholder = `Write a note on this ${noun}...`;

  if (!expanded) {
    return (
      <div className="tw:flex tw:max-h-11 tw:w-full tw:items-center tw:rounded-lg tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface)] tw:px-4 tw:py-2.5 tw:transition-colors tw:hover:border-[var(--color-text-muted)]">
        <button
          type="button"
          aria-label={placeholder}
          onClick={() => setExpanded(true)}
          className="tw:flex-1 tw:cursor-text tw:text-left tw:text-sm tw:text-[var(--color-text-muted)] tw:select-none"
        >
          {placeholder}
        </button>
        {enabledTemplates.length > 0 && (
          <>
            <span className="tw:mx-3 tw:h-4 tw:w-px tw:bg-[var(--color-border)]" />
            <TemplatePicker
              templates={enabledTemplates}
              onSelect={handleTemplateSelect}
              placeholder="Start from a template"
              icon={WandSparkles}
              className="tw:h-auto tw:w-auto tw:cursor-pointer tw:border-0 tw:bg-transparent tw:py-0 tw:pr-1 tw:pl-0 tw:text-sm tw:text-[var(--color-text-secondary)] tw:shadow-none tw:hover:text-[var(--color-highlight)] tw:[&_[data-slot=input-group-control]]:h-auto tw:[&_[data-slot=input-group-control]]:py-0 tw:[&_[data-slot=input-group-addon]]:py-0 tw:[&_[data-slot=input-group-button]]:size-auto"
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div
      ref={formWrapperRef}
      onKeyDown={handleKeyDown}
      // Not a portal into the pane: a remount would lose the pending draft
      // save, uploads, category, and undo history.
      className={cn(
        "tw:w-full",
        // An edit sits inside the note's own card, which already frames it.
        !isEditing &&
          "tw:rounded-lg tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface)] tw:p-4",
        // Full screen leaves the card behind, so it brings its own surface.
        isFullscreen &&
          "tw:absolute tw:inset-0 tw:z-50 tw:flex tw:flex-col tw:rounded-none tw:border-0 tw:bg-[var(--color-surface)] tw:p-6"
      )}
    >
      <form onSubmit={handleSubmit} className={fullscreenColumnClass}>
        <RichTextEditor
          ref={editor}
          className={fullscreenColumnClass}
          contentClassName={cn(
            "tw:overflow-y-auto",
            isFullscreen ? "tw:flex-1 tw:overscroll-contain" : "tw:max-h-96"
          )}
          content={initialContent ?? templateContent}
          editorState={initialEditorState ?? initialDraft}
          onStateChange={handleEditorStateChange}
          placeholder={placeholder}
          autoFocus
          onAttach={() => fileInputRef.current.click()}
          templates={enabledTemplates}
          onSelectTemplate={handleTemplateSelect}
          categories={categories}
          categoryValue={formData.categoryId}
          onChangeCategory={value =>
            handleFormDataChange({ categoryId: value })
          }
          onManageCategories={() => setManageOpen(true)}
          isFullscreen={isFullscreen}
          onToggleFullscreen={
            canFullscreen ? () => setIsFullscreen(open => !open) : undefined
          }
        />

        <div className="tw:mt-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <ManageCategoriesModal
            open={manageOpen}
            onOpenChange={setManageOpen}
            categories={categories}
            fetchCategories={updateCategories}
          />

          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            aria-label="Attach files"
            onChange={handleFileChange}
          />

          {showIncludeInEntity && (
            <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-sm">
              <Switch
                id={includeInEntityId}
                checked={formData.includeInEntity}
                onCheckedChange={checked =>
                  handleFormDataChange({ includeInEntity: !!checked })
                }
              />
              <label
                htmlFor={includeInEntityId}
                className="tw:cursor-pointer tw:select-none"
              >
                Also show on {entityName}
              </label>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      aria-label="What does this do?"
                      className="tw:inline-flex tw:text-[var(--color-text-muted)] tw:hover:text-[var(--color-text-secondary)]"
                    >
                      <Info className="tw:size-3.5" />
                    </button>
                  }
                />
                <TooltipContent>
                  This note will also appear on the entity&apos;s timeline.
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          <div className="tw:ml-auto tw:flex tw:items-center tw:gap-3">
            <span
              role="status"
              aria-live="polite"
              className="tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-[var(--color-text-muted)]"
            >
              {DRAFT_STATUS_MESSAGE[draftStatus]}
            </span>
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
            <SaveButton
              type="submit"
              size="sm"
              saving={submitting || isUploading}
              savingText={isUploading ? "Uploading" : "Saving"}
              disabled={!editorHasText || hasFailedUploads}
            />
          </div>
        </div>

        <UploadAttachmentList
          uploads={uploads}
          existing={isEditing ? note.attachments : []}
          onRemove={uploadManager.removeUpload}
          onRetry={uploadManager.retryUpload}
        />
      </form>
    </div>
  );
}

NoteForm.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.number,
    category_id: PropTypes.number,
    editor_state: PropTypes.string,
    rich_text: PropTypes.string,
    info: PropTypes.object,
    attachments: PropTypes.arrayOf(PropTypes.object),
  }),
  modelType: PropTypes.string.isRequired,
  modelId: PropTypes.number.isRequired,
  slug: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onDirtyChange: PropTypes.func,
  updateCategories: PropTypes.func,
  categories: PropTypes.arrayOf(PropTypes.object),
  refersToModel: PropTypes.string,
  refersToID: PropTypes.number,
  callSid: PropTypes.string,
};

export default NoteForm;
